import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, MapPin, Mail, Phone, User } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

export default function VenueDetail() {
  const { id } = useParams();
  const [venue, setVenue] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pnl, setPnl] = useState([]);

  useEffect(() => {
    (async () => {
      const [v, t, e, a, r, ex, act, p] = await Promise.all([
        api.get(`/venues/${id}`),
        api.get("/tasks"), api.get("/events"), api.get("/academies"),
        api.get("/revenue"), api.get("/expenses"), api.get("/activities", { params: { limit: 100 } }),
        api.get(`/venues/${id}/pnl`, { params: { months: 6 } }),
      ]);
      setVenue(v.data);
      setTasks(t.data.filter((x) => x.venue_id === id));
      setEvents(e.data.filter((x) => x.venue_id === id));
      setAcademies(a.data.filter((x) => x.venue_id === id));
      setRevenue(r.data.filter((x) => x.venue_id === id));
      setExpenses(ex.data.filter((x) => x.venue_id === id));
      setActivities(act.data.filter((x) => x.entity_id === id || x.entity_type === "venues"));
      setPnl(p.data.map((m) => ({ ...m, label: new Date(m.month + "-01").toLocaleDateString("en-IN", { month: "short" }) })));
    })();
  }, [id]);

  if (!venue) return <div className="text-slate-500">Loading venue...</div>;
  const revTotal = revenue.reduce((s, r) => s + Number(r.amount || 0), 0);
  const expTotal = expenses.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="venue-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <Link to="/venues" className="text-xs text-blue-600 hover:underline">← Back to venues</Link>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mt-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {venue.name}
          </h1>
          <div className="text-sm text-slate-500 flex items-center gap-2 mt-1">
            <MapPin className="h-3 w-3" /> {venue.address}
          </div>
        </div>
        <StatusBadge status={venue.status} />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger data-testid="venue-tab-overview" value="overview">Overview</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-sports" value="sports">Sports & Pricing</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-tasks" value="tasks">Tasks</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-events" value="events">Events</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-academies" value="academies">Academies</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-finance" value="finance">Finance</TabsTrigger>
          <TabsTrigger data-testid="venue-tab-activity" value="activity">Activity</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border-slate-200"><CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-900">Details</h3>
              <div className="text-sm text-slate-700 space-y-2">
                <div><span className="text-slate-500">Partner:</span> {venue.partner || "—"}</div>
                <div><span className="text-slate-500">Location:</span> {venue.location}</div>
                <div><span className="text-slate-500">Start date:</span> {fmtDate(venue.start_date)}</div>
                <div className="flex items-center gap-2"><User className="h-3 w-3 text-slate-400" /> {venue.contact_name || "—"}</div>
                <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-slate-400" /> {venue.phone || "—"}</div>
                <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-slate-400" /> {venue.email || "—"}</div>
              </div>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-6 space-y-3">
              <h3 className="font-bold text-slate-900">Snapshot</h3>
              <div className="grid grid-cols-2 gap-4">
                <div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Tasks</div><div className="text-xl font-black mt-1">{tasks.length}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Events</div><div className="text-xl font-black mt-1">{events.length}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Revenue</div><div className="text-xl font-black mt-1 text-emerald-700">{fmtINR(revTotal)}</div></div>
                <div><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Expenses</div><div className="text-xl font-black mt-1 text-rose-700">{fmtINR(expTotal)}</div></div>
              </div>
            </CardContent></Card>
          </div>
        </TabsContent>

        <TabsContent value="sports">
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Sport</TableHead><TableHead>Courts / Fields</TableHead><TableHead className="text-right">Hourly Price</TableHead><TableHead>Operating Hours</TableHead></TableRow></TableHeader>
            <TableBody>
              {(venue.sports || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-6">No sports configured yet.</TableCell></TableRow>}
              {(venue.sports || []).map((s, i) => (
                <TableRow key={i}><TableCell className="font-medium">{s.sport}</TableCell><TableCell>{s.courts}</TableCell><TableCell className="text-right font-mono">{fmtINR(s.price)}</TableCell><TableCell>{s.hours}</TableCell></TableRow>
              ))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="tasks">
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Title</TableHead><TableHead>Assignee</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Due</TableHead></TableRow></TableHeader>
            <TableBody>
              {tasks.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">No tasks linked to this venue.</TableCell></TableRow>}
              {tasks.map((t) => (<TableRow key={t.id}><TableCell>{t.title}</TableCell><TableCell>{t.assignee_name}</TableCell><TableCell><StatusBadge status={t.priority} /></TableCell><TableCell><StatusBadge status={t.status} /></TableCell><TableCell>{fmtDate(t.due_date)}</TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="events">
          <div className="grid md:grid-cols-2 gap-4">
            {events.length === 0 && <div className="text-slate-500 text-sm">No events scheduled at this venue.</div>}
            {events.map((e) => (
              <Link key={e.id} to={`/events/${e.id}`} className="block bg-white border border-slate-200 rounded-md p-4 hover:shadow-sm">
                <div className="flex justify-between"><div className="font-semibold">{e.name}</div><StatusBadge status={e.status} /></div>
                <div className="text-xs text-slate-500 mt-1">{e.type} · {fmtDate(e.date)}</div>
              </Link>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="academies">
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Sport</TableHead><TableHead>Coach</TableHead><TableHead>Schedule</TableHead><TableHead>Students</TableHead><TableHead className="text-right">Fee</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {academies.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No academies yet.</TableCell></TableRow>}
              {academies.map((a) => (<TableRow key={a.id}><TableCell>{a.sport}</TableCell><TableCell>{a.coach}</TableCell><TableCell>{a.schedule}</TableCell><TableCell>{a.student_count}</TableCell><TableCell className="text-right font-mono">{fmtINR(a.fee)}</TableCell><TableCell><StatusBadge status={a.status} /></TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="finance">
          <div className="space-y-6">
            <Card className="border-slate-200"><CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-slate-900">Revenue vs Expenses (last 6 months)</h3>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">₹ INR</div>
              </div>
              {pnl.every((m) => m.revenue === 0 && m.expenses === 0) ? (
                <div className="text-sm text-slate-500 py-8 text-center">No monthly finance data recorded for this venue yet.</div>
              ) : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart data={pnl} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" stroke="#94a3b8" fontSize={12} />
                      <YAxis stroke="#94a3b8" fontSize={12} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v) => fmtINR(v)} labelStyle={{ color: "#0f172a" }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="revenue" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} name="Revenue" />
                      <Line type="monotone" dataKey="expenses" stroke="#e11d48" strokeWidth={2} dot={{ r: 3 }} name="Expenses" />
                      <Line type="monotone" dataKey="profit" stroke="#2563eb" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} name="Profit" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent></Card>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="border-slate-200"><CardContent className="p-6">
                <h3 className="font-bold text-slate-900 mb-3">Revenue at this venue</h3>
                <div className="text-2xl font-black text-emerald-700 mb-4">{fmtINR(revTotal)}</div>
                <ul className="space-y-2 text-sm">
                  {revenue.length === 0 && <li className="text-slate-500">No revenue records.</li>}
                  {revenue.map((r) => <li key={r.id} className="flex justify-between"><span>{r.description}</span><span className="font-mono">{fmtINR(r.amount)}</span></li>)}
                </ul>
              </CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-6">
                <h3 className="font-bold text-slate-900 mb-3">Expenses at this venue</h3>
                <div className="text-2xl font-black text-rose-700 mb-4">{fmtINR(expTotal)}</div>
                <ul className="space-y-2 text-sm">
                  {expenses.length === 0 && <li className="text-slate-500">No expense records.</li>}
                  {expenses.map((r) => <li key={r.id} className="flex justify-between"><span>{r.description}</span><span className="font-mono">{fmtINR(r.amount)}</span></li>)}
                </ul>
              </CardContent></Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="border-slate-200"><CardContent className="p-6">
            <ul className="space-y-2 text-sm">
              {activities.length === 0 && <li className="text-slate-500">No activity yet.</li>}
              {activities.map((a) => (
                <li key={a.id}><span className="font-semibold">{a.actor_name}</span> {a.action} · <span className="text-slate-500">{fmtDate(a.created_at)}</span></li>
              ))}
            </ul>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
